import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

/**
 * Ko-fi support entry points.
 *
 * Phase 1 is a link-out: the panel sets the mood, Ko-fi collects the money.
 * Nothing here touches payments — no SDK, no webhook, no record of who gave
 * what. Clicking through opens Ko-fi in a new tab and closes the panel. There
 * is deliberately no "thank you" state, because we have no way to know whether
 * the payment actually happened and a confirmation we can't verify is a lie.
 *
 * The amounts are framing, not a real selection — Ko-fi's own page is where
 * the number gets chosen. They exist to give the ask a shape.
 *
 * Desktop gets a centred panel; mobile gets a bottom sheet, which is the
 * pattern people expect on a phone. Same content either way.
 */

const KOFI_USERNAME = import.meta.env.VITE_KOFI_USERNAME || 'reanlo';

if (!import.meta.env.VITE_KOFI_USERNAME && typeof console !== 'undefined') {
  console.warn('VITE_KOFI_USERNAME is not set — falling back to "reanlo".');
}

const KOFI_URL = `https://ko-fi.com/${KOFI_USERNAME}`;

const C = {
  gold:       '#d4a84b',
  goldSoft:   'rgba(212,168,75,0.10)',
  goldLine:   'rgba(212,168,75,0.45)',
  forest:     '#2d4a3e',
  cream:      '#f5f0e8',
  rust:       '#b85c38',
  ink:        '#292524',
  grey:       '#78716c',
};

const TIERS = [
  { amount: 5,  label: 'a coffee',    note: 'One less yawn' },
  { amount: 15, label: 'a paperback', note: 'A good afternoon' },
  { amount: 25, label: 'a hardcover', note: 'The whole shelf thanks you' },
];

const MOBILE_MAX = 767;

function openKofi() {
  window.open(KOFI_URL, '_blank', 'noopener,noreferrer');
}

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= MOBILE_MAX : false
  );
  useEffect(() => {
    const on = () => setIsMobile(window.innerWidth <= MOBILE_MAX);
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);
  return isMobile;
}

/* ────────────────────────────────────────────────────────────────────────────
   Coffee mark — SVG rather than the emoji, which renders differently on every
   platform and sits off-centre inside a small circle.
   ──────────────────────────────────────────────────────────────────────────── */
export function CoffeeIcon({ size = 16, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden style={{ flexShrink: 0 }}>
      <path d="M17 8h1a4 4 0 1 1 0 8h-1" />
      <path d="M3 8h14v9a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4Z" />
      <line x1="6" y1="2" x2="6" y2="5" />
      <line x1="10" y1="2" x2="10" y2="5" />
      <line x1="14" y1="2" x2="14" y2="5" />
    </svg>
  );
}

/* ────────────────────────────────────────────────────────────────────────────
   The panel — bottom sheet on mobile, centred on desktop
   ──────────────────────────────────────────────────────────────────────────── */
export function SupportModal({ open, onClose }) {
  const [selected, setSelected] = useState(15);
  const [note, setNote] = useState('');
  const isMobile = useIsMobile();

  // Escape closes, and the page behind stops scrolling while it's up.
  useEffect(() => {
    if (!open) return;
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  function handleSupport() {
    openKofi();
    onClose();
  }

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Support Reanlo"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 10050,
        background: 'rgba(41,37,36,0.55)',
        display: 'flex',
        alignItems: isMobile ? 'flex-end' : 'center',
        justifyContent: 'center',
        padding: isMobile ? 0 : 20,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: isMobile ? '100%' : 420,
          maxHeight: isMobile ? '90%' : undefined,
          overflowY: isMobile ? 'auto' : undefined,
          boxSizing: 'border-box',
          background: 'var(--brand-white)',
          borderRadius: isMobile ? '22px 22px 0 0' : 18,
          overflow: isMobile ? 'auto' : 'hidden',
          boxShadow: isMobile
            ? '0 -8px 30px rgba(41,37,36,0.28)'
            : '0 24px 60px rgba(41,37,36,0.28)',
          // Keeps the button clear of the home indicator on newer phones.
          paddingBottom: isMobile ? 'env(safe-area-inset-bottom)' : 0,
        }}
      >
        {/* Grab handle — mobile only, signals the sheet is dismissable */}
        {isMobile && (
          <div style={{ width: 40, height: 4, background: '#d8ccb4', borderRadius: 'var(--radius-pill)', margin: '10px auto 0' }} />
        )}

        {/* Header */}
        <div style={{
          background: isMobile ? 'var(--brand-white)' : C.forest,
          padding: isMobile ? '14px 22px 6px' : '26px 26px 22px',
          position: 'relative',
        }}>
          {!isMobile && (
            <button
              onClick={onClose}
              aria-label="Close"
              style={{
                position: 'absolute', top: 14, right: 14, width: 28, height: 28,
                borderRadius: 'var(--radius-circle)', border: 'none', cursor: 'pointer',
                background: 'rgba(245,240,232,0.14)', color: C.cream, fontSize: 15, lineHeight: 1,
              }}
            >
              ×
            </button>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: isMobile ? 6 : 10 }}>
            <CoffeeIcon size={isMobile ? 22 : 26} color={isMobile ? C.rust : C.gold} />
            <h2 style={{
              fontFamily: 'var(--font-display, Georgia, serif)',
              fontSize: isMobile ? 22 : 22, fontWeight: 700,
              color: isMobile ? C.forest : C.cream, margin: 'var(--space-none)', lineHeight: 1.15,
            }}>
              Buy the shelf a coffee
            </h2>
          </div>
          <p style={{
            fontSize: isMobile ? 12.5 : 13.5, lineHeight: 1.6,
            color: isMobile ? C.grey : 'rgba(245,240,232,0.75)', margin: 'var(--space-none)',
          }}>
            Reanlo is built by one very caffeinated founder. Your support goes
            straight into new features and making discovery even better.
          </p>
        </div>

        {/* Tiers */}
        <div style={{ padding: isMobile ? '16px 22px 24px' : '20px 26px 26px' }}>
          <div style={{ display: 'flex', gap: 'var(--space-10)', marginBottom: 18 }}>
            {TIERS.map(t => {
              const isOn = selected === t.amount;
              return (
                <button
                  key={t.amount}
                  onClick={() => setSelected(t.amount)}
                  aria-pressed={isOn}
                  style={{
                    flex: 1, boxSizing: 'border-box', cursor: 'pointer',
                    padding: '13px 6px', borderRadius: 'var(--radius-lg)', textAlign: 'center',
                    background: isOn ? C.goldSoft : 'transparent',
                    border: `1.5px solid ${isOn ? C.gold : '#e8e4dc'}`,
                    transition: 'background 0.15s, border-color 0.15s',
                  }}
                >
                  <div style={{
                    fontFamily: 'var(--font-display, Georgia, serif)',
                    fontSize: isMobile ? 19 : 17, fontWeight: 700,
                    color: isOn ? C.gold : C.ink,
                  }}>
                    ${t.amount}
                  </div>
                  <div style={{ fontSize: 10.5, color: C.grey, marginTop: 'var(--space-xxs)' }}>{t.label}</div>
                </button>
              );
            })}
          </div>

          <p style={{ fontSize: 'var(--font-size-sm)', color: C.grey, margin: '0 0 14px', textAlign: 'center', minHeight: 16 }}>
            {TIERS.find(t => t.amount === selected)?.note}
          </p>

          {/* Decorative in Phase 1. The link-out carries nothing with it, so
              whatever is typed here does not reach Ko-fi — Ko-fi's own page has
              a message box. Wire this up in Phase 2 if the embed can carry it. */}
          <input
            value={note}
            onChange={e => setNote(e.target.value)}
            placeholder="leave a shelf note (optional)"
            aria-label="Leave a shelf note"
            style={{
              width: '100%', boxSizing: 'border-box', marginBottom: 'var(--space-14)',
              padding: '12px 14px', borderRadius: 'var(--radius-10)',
              border: '1.5px dashed rgba(139,115,85,0.4)',
              background: 'transparent', fontSize: 'var(--font-size-base)', outline: 'none',
              fontFamily: 'inherit', color: C.ink,
            }}
          />

          <button
            onClick={handleSupport}
            style={{
              width: '100%', padding: isMobile ? '14px 0' : '13px 0',
              borderRadius: 'var(--radius-lg)', border: 'none',
              background: C.gold, color: C.ink,
              fontSize: isMobile ? 15 : 14.5, fontWeight: 700, cursor: 'pointer',
            }}
          >
            Support with ${selected}
          </button>

          <p style={{ fontSize: 'var(--font-size-xs)', color: C.grey, margin: '12px 0 0', textAlign: 'center', lineHeight: 1.5 }}>
            Opens Ko-fi in a new tab.
          </p>
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ────────────────────────────────────────────────────────────────────────────
   Header pill — desktop
   ──────────────────────────────────────────────────────────────────────────── */
export function SupportPill({ compact = false }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        title="Keep the shelf stocked"
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 'var(--space-6)',
          padding: compact ? '5px 10px' : '6px 13px',
          borderRadius: 'var(--radius-pill)', whiteSpace: 'nowrap', cursor: 'pointer',
          background: 'transparent', border: `1.5px solid ${C.goldLine}`,
          color: '#8a6d28', fontSize: compact ? 12 : 12.5, fontWeight: 600,
          lineHeight: 1.2, transition: 'background 0.15s, border-color 0.15s',
        }}
        onMouseEnter={e => { e.currentTarget.style.background = C.goldSoft; e.currentTarget.style.borderColor = C.gold; }}
        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = C.goldLine; }}
      >
        <CoffeeIcon size={compact ? 13 : 14} color="#8a6d28" />
        {compact ? 'Support' : 'Keep the shelf stocked'}
      </button>
      <SupportModal open={open} onClose={() => setOpen(false)} />
    </>
  );
}

/* ────────────────────────────────────────────────────────────────────────────
   Icon button — mobile. No room for a label beside the avatar, so it's the
   coffee alone in a ring, sitting before the avatar in the header.
   ──────────────────────────────────────────────────────────────────────────── */
export function SupportIconButton() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        aria-label="Support the shelf"
        title="Support the shelf"
        style={{
          width: 36, height: 36, borderRadius: 'var(--radius-circle)', padding: 'var(--space-none)',
          background: 'transparent', border: `1.5px solid ${C.goldLine}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', flexShrink: 0,
        }}
      >
        <CoffeeIcon size={17} color={C.rust} />
      </button>
      <SupportModal open={open} onClose={() => setOpen(false)} />
    </>
  );
}
